import AuthComponent from "@components/auth/component";
import CustomButton from "@components/buttons/CustomButton";
import ReportFilter from "@components/filters/ReportFilter";
import PageWrapper from "@components/layouts/PageWrapper";
import Search from "@components/layouts/Search";
import useGetAllInventoryReport from "@hooks/report/useGetAllInventoryReport";
import usePagination from "@hooks/usePagination";
import useSearch from "@hooks/useSearch";
import React, { useRef } from "react";
import { formatFilterDate } from "@utils/general/format-filter-date";
import { useDraggable } from "react-use-draggable-scroll";
import InventoryTable from "@components/report/InventoryTable";
import DeliveryOrderItemTable from "@components/report/DeliveryOrderItemTable";
import { exportToExcel } from "@utils/general/excel-export";
import * as XLSX from "xlsx";

import { saveAs } from "file-saver";
import { useGetAllDeliveryOrderByItemQuery } from "@states/report/report.api";
import useGetAllIDeliveryOrderByItemReport from "@hooks/report/useGetAllIDeliveryOrderByItemReport";
import { useGetAllProductsQuery } from "@states/product/product.api";
function DeliveryOrdersByItemReportPage() {
  const { page, limit, handlePageChange, handlePerRowsChange } =
    usePagination();
  const { search, setSearch } = useSearch();
  const today = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(today.getMonth() - 1);
  const filterRef = useRef({
    from_date: undefined,
    to_date: undefined,
  });

  const {
    inventoryReportError,
    inventoryReportRefetch,
    inventoryReportFetching,
    inventoryReportPaginator,
    inventoryReportData,
  } = useGetAllIDeliveryOrderByItemReport({
    //@ts-ignore
    from_date: filterRef.current?.from_date || formatFilterDate(lastMonth),
    //@ts-ignore
    to_date: filterRef.current?.to_date || formatFilterDate(today),
  });
  const { data: allProductsData } = useGetAllProductsQuery({
    page: 1,
    limit: 1000,
  });

  const productLookup = React.useMemo(() => {
    const products = allProductsData?.data || [];
    const lookup = new Map<string, any>();

    products.forEach((product: any) => {
      if (product?.product_id != null) {
        lookup.set(`id:${product.product_id}`, product);
      }

      const fullCode = product?.product_code_prefix
        ? `${product.product_code_prefix}${product.product_code}`
        : product?.product_code;

      if (fullCode) {
        lookup.set(`code:${fullCode}`, product);
      }
    });

    return lookup;
  }, [allProductsData]);

  const getUnitLabel = (product: any) => {
    const fullCode = product?.product_code_prefix
      ? `${product.product_code_prefix}${product.product_code}`
      : product?.product_code;
    const matchedProduct =
      (product?.product_id != null &&
        productLookup.get(`id:${product.product_id}`)) ||
      (fullCode && productLookup.get(`code:${fullCode}`));

    return (
      product?.unit_name ||
      product?.base_unit_name ||
      product?.unit?.unit_name ||
      product?.unit ||
      matchedProduct?.unit_name ||
      matchedProduct?.base_unit_name ||
      "-"
    );
  };

  const normalizedInventoryReportData = React.useMemo(() => {
    if (!inventoryReportData) return inventoryReportData;

    return {
      ...inventoryReportData,
      items: (inventoryReportData.items || []).map((product: any) => ({
        ...product,
        unit_name: getUnitLabel(product),
      })),
    };
  }, [inventoryReportData, productLookup]);

  const handleExportClick = () => {
    if (
      !normalizedInventoryReportData?.items ||
      normalizedInventoryReportData.items.length === 0
    )
      return;

    const allocationAccountNames = Array.from(
      new Set(
        (normalizedInventoryReportData.warehouses || [])
          .map((w: any) => w.warehouse_name)
          .filter(Boolean)
      )
    );

    const rows = normalizedInventoryReportData.items.map((product: any) => {
      const row: any = {
        "Product Code": product.product_code_prefix
          ? `${product.product_code_prefix}${product.product_code}`
          : product.product_code,
        "Product Name": product.product_name,
        Unit: getUnitLabel(product),
      };

      allocationAccountNames.forEach((name) => {
        const wh = (product.warehouses || []).find(
          (w: any) => w.warehouse_name === name
        );
        row[name] = wh ? wh.total_base_qty : 0;
      });

      return row;
    });

    const totalRow: any = {
      "Product Code": "Total",
      "Product Name": "",
      Unit: "",
    };

    allocationAccountNames.forEach((name) => {
      totalRow[name] = normalizedInventoryReportData.items.reduce(
        (sum: number, product: any) => {
          const wh = (product.warehouses || []).find(
            (warehouse: any) => warehouse.warehouse_name === name
          );
          return sum + Number(wh?.total_base_qty || 0);
        },
        0
      );
    });

    const worksheet = XLSX.utils.json_to_sheet([...rows, totalRow]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Delivery Orders By Item"
    );

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(blob, "delivery_orders_by_item_report.xlsx");
  };

  console.log("data", inventoryReportData);
  return (
    <AuthComponent
      action="view"
      module="Report"
      subModule="Delivery Orders By Item"
      page
    >
      <>
        <Search setSearch={setSearch} />
        <PageWrapper title="Delivery Orders By Item Report">
          <div className="grid grid-cols-8 justify-evenly">
            <div className="col-span-6">
              {/* @ts-ignore */}
              <ReportFilter
                refetch={inventoryReportRefetch}
                filterRef={filterRef}
              />
            </div>
            <div className=" flex items-center justify-end  mt-2 col-span-2">
              <CustomButton
                title={
                  <div className="flex  gap-3">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.4126 17.2325V1H10.102C6.73567 1.60819 3.37848 2.17076 0 2.75462V18.1083C3.32374 18.6952 6.69006 19.2821 10.0351 19.8903H11.4035V17.2325H11.4126ZM6.46198 13.8632C6.04269 12.9666 5.67515 12.0467 5.36117 11.1081C5.05707 11.9991 4.63742 12.8384 4.2938 13.7111C3.81333 13.7111 3.33286 13.6837 2.8524 13.6655C3.41497 12.5616 3.9593 11.4486 4.54011 10.3539C4.04748 9.22268 3.50316 8.11579 2.99532 6.99368L4.44889 6.91158C4.77427 7.76912 5.1331 8.61754 5.40374 9.49637C5.70783 8.5628 6.12444 7.67181 6.49544 6.78386C6.99212 6.74939 7.48982 6.71898 7.98853 6.69263C7.40467 7.89278 6.81575 9.09598 6.22175 10.3022C6.82994 11.5186 7.43813 12.7502 8.04631 13.9787C7.51719 13.9361 6.99111 13.9027 6.46198 13.8632Z"
                        fill="white"
                      />
                      <path
                        d="M11.9658 3.10352V4.26819H14.2921V5.98632H11.9658V6.92901H14.2921V8.64714H11.9658V9.58983H14.2921V11.3049H11.9658V12.2476H14.2921V13.9505H11.9658V14.948H14.2921V16.6113H11.9658V17.776H20V3.10352H11.9658ZM18.2271 16.6235H14.8456V14.9601H18.2271V16.6235ZM18.2271 13.9627H14.8456V12.2446H18.2271V13.9627ZM18.2271 11.3019H14.8456V9.58679H18.2271V11.3019ZM18.2271 8.64106H14.8456V6.92597H18.2271V8.64106ZM18.2271 5.98024H14.8456V4.26819H18.2271V5.98024Z"
                        fill="white"
                      />
                    </svg>
                    Export Excel
                  </div>
                }
                color="bg-[#1E6E42] h-3/6"
                handleClick={handleExportClick}
                className=""
              />
            </div>
          </div>
          {/* <InventoryTable inventoryReportData={inventoryReportData} /> */}
          <DeliveryOrderItemTable data={normalizedInventoryReportData} />
        </PageWrapper>
      </>
    </AuthComponent>
  );
}

export default DeliveryOrdersByItemReportPage;
